import React, { useState } from 'react';
import { get, uniqBy, find, head } from 'lodash';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import ConfigPage from '../ConfigPage';
import { useModelInfo } from '../../../common/ModelInfoContext';
import { TRIMS } from '../../../common/steps';
import TrimWithoutFiltering from './TrimWithoutFiltering/TrimWithoutFiltering';
import TrimWithFiltering from './TrimWithFiltering/TrimWithFiltering';
import {
  buildCgiImageForCard,
  buildCgiImageForCompare,
} from '../../../api/config/normalizeCGI';
import YourTrim from './yourTrim/YourTrim';

const NormalizeAndFilterByConfiguration = (props) => {
  const { children, matrix, styles, appResources } = props;

  const { trims, bodyTypes, engines } = matrix;
  const normalizeTrims = trims.map((trim) => {
    const bodyType = find(bodyTypes, {
      id: trim.bodyTypeId,
    });

    const styleByTrimName = find(styles, { trimName: trim.trimName });
    const driveType = find(bodyType.driveTypes, {
      driveType: trim.driveType.id,
    });

    const options = trim.configurations.map((config) => {
      const engine = find(engines, {
        id: config.engine.code,
      });

      const transmission = find(matrix.transmissions, {
        id: config.transmission.code,
      });

      return {
        ...config,
        bodyType: get(trim, 'bodyTypeId'),
        trimName: trim.trimName,
        msrp: get(config, 'msrp.amount.value', ''),
        message: get(config, 'msrp.unknown', ''),
        msrpText: get(config, 'msrp.amount.text', ''),
        driveType: trim.driveType.id,
        styleId: trim.styleId,
        cabSize: get(bodyType, 'cabSize', ''),
        rearWheelType: get(bodyType, 'rearWheelType', ''),
        driveTypeLabel: get(driveType, 'label', ''),
        driveTypeDescription: get(driveType, 'description', ''),
        engineCode: get(config, 'engine.code'),
        engineType: get(config, 'engine.type'),
        engineLabel: get(config, 'engine.label'),
        engineDescription: get(engine, 'description', ''),
        engineLongCFD: get(engine, 'longCfd', ''),
        engineLargeImageUrl: get(engine, 'largeImageUrl', ''),
        engineDisclaimer: get(engine, 'disclaimer', ''),
        transmissionCode: get(config, 'transmission.code', ''),
        transmissionDescription: get(transmission, 'description', ''),
        transmissionLongCFD: get(transmission, 'longCfd', ''),
        transmissionLargeImageUrl: get(transmission, 'largeImageUrl', ''),
        transmissionDisclaimer: get(transmission, 'disclaimer', ''),
        transmissionType: get(config, 'transmission.type'),
        transmissionLabel: get(config, 'transmission.label'),
        eligibleForInvoicePrice: get(
          styleByTrimName,
          'eligibleForInvoicePrice',
          true
        ),
        subHeader: get(bodyType, 'subHeader'),
      };
    });

    const configurationWithLowestPrice = head(get(trim, 'configurations'));
    const cgiSizes = get(appResources, 'cgiSize');
    return {
      ...trim,
      ss: get(configurationWithLowestPrice, 'ss'),
      cgi: buildCgiImageForCard(trim.cgiImageUrl, cgiSizes),
      cgiTransparent: buildCgiImageForCompare(trim.cgiImageUrl, cgiSizes),
      rearWheelType: get(bodyType, 'rearWheelType', ''),
      options: uniqBy(
        options,
        (x) =>
          `${x.bodyType}${x.driveType}${x.rearWheelType}${x.trimName}${x.engine.code}${x.transmission.code}`
      ),
    };
  });

  return children(normalizeTrims);
};

const Trims = (props) => {
  const {
    matrix,
    config,
    features,
    bottomOffset,
    animate,
    animationCallback,
    appResources,
    reservation,
    setBottomOffset,
  } = props;
  const { enableConfigStep } = useModelInfo();
  const [trimInnerKey, setTrimInnerKey] = useState(1);
  const updateTrimInnerKey = () => setTrimInnerKey(trimInnerKey + 1);
  const animation = { animate, animationCallback, updateTrimInnerKey };

  if (reservation?.preOrderStyleId) {
    return (
      <YourTrim
        features={features}
        matrix={matrix}
        styleId={reservation.preOrderStyleId}
        appResources={appResources}
        bottomOffset={bottomOffset}
        setBottomOffset={setBottomOffset}
        config={config}
      />
    );
  }

  return (
    <ConfigPage step={TRIMS}>
      {({ onTrimChange, isUpdating, updatingOption, displayTrimFilters }) => (
        <NormalizeAndFilterByConfiguration
          filter={enableConfigStep}
          matrix={matrix}
          styles={config.styles}
          selections={config.selections}
          appResources={appResources}>
          {(trims) => (
            <>
              {displayTrimFilters ? (
                <TrimWithFiltering
                  {...props}
                  animation={animation}
                  styles={config.styles}
                  trims={trims}
                  onChange={onTrimChange}
                  isUpdating={isUpdating}
                  updatingOption={updatingOption}
                  features={features}
                  bottomOffset={bottomOffset}
                  trimMatrix={matrix}
                />
              ) : (
                <TrimWithoutFiltering
                  {...props}
                  animation={animation}
                  styles={config.styles}
                  trims={trims}
                  onChange={onTrimChange}
                  isUpdating={isUpdating}
                  updatingOption={updatingOption}
                  features={features}
                  bottomOffset={bottomOffset}
                  trimMatrix={matrix}
                />
              )}
            </>
          )}
        </NormalizeAndFilterByConfiguration>
      )}
    </ConfigPage>
  );
};

Trims.propTypes = {
  matrix: PropTypes.object.isRequired,
  config: PropTypes.object.isRequired,
  disclaimer: PropTypes.string,
  appResources: PropTypes.object.isRequired,
  breakpoint: PropTypes.string,
  disableCompare: PropTypes.bool,
  isSelectionRevealed: PropTypes.bool,
};

const mapStateToProps = (state) => {
  const {
    config,
    matrix,
    breakpoint,
    appResources,
    isSelectionRevealed,
    params,
  } = state;
  const { modelMatrix } = config;
  const postalCode = params.postalCode;
  return {
    config,
    matrix,
    breakpoint,
    appResources,
    isSelectionRevealed,
    postalCode,
    modelMatrix,
  };
};

export default connect(mapStateToProps)(Trims);
